/**
 * Photos department landing page. Shows aggregate stats from
 * /image-index/overview scoped to a selectable date range
 * (last 7 days, yesterday, last month, or a custom from/to).
 * Range selection is mirrored into the URL search params so the
 * page is shareable and survives reload.
 */
import React, { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import Layout from "../../components/Layout"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { THEME } from "../../config/theme"
import { tint } from "../../utils/color"
import {
  RooftopOption,
  getOverview,
  listRooftops,
  OverviewQuery,
  PhotosOverview,
} from "../../api/imageIndex"

type RangePreset = "today" | "yesterday" | "last-7-days" | "last-month" | "custom"

const RANGE_OPTIONS: { value: RangePreset; label: string }[] = [
  { value: "today",       label: "Today" },
  { value: "yesterday",   label: "Yesterday" },
  { value: "last-7-days", label: "Last 7 days" },
  { value: "last-month",  label: "Last month" },
  { value: "custom",      label: "Custom range" },
]

const DEFAULT_RANGE: RangePreset = "today"
const ALL_ROOFTOPS_VALUE = "__all__"

interface StatSpec {
  label: string
  value: number | null
  // Null means a plain tile (no colored left edge, no body tint).
  // Reserved-for-actionable tiles (Complete/Partial/Ready/Pushed/Failed)
  // get a color; raw counts (Total Sessions, Units Captured) stay neutral.
  accentColor: string | null
  // Renders a thin silver divider after this tile. Used to separate
  // semantic tile groups (e.g. photo-count buckets vs. lifecycle states).
  dividerAfter?: boolean
}

export default function PhotosOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  // It's a tool: require a concrete dealer group; the aggregates are scoped to it.
  const { dealerGroupId } = useDealerGroup()

  const range = (searchParams.get("range") as RangePreset | null) ?? DEFAULT_RANGE
  const customFrom = searchParams.get("from") ?? ""
  const customTo = searchParams.get("to") ?? ""
  const rooftopSlug = searchParams.get("rooftop") ?? ALL_ROOFTOPS_VALUE

  const resolvedQuery = useMemo<OverviewQuery>(() => {
    const range_dates = resolveRange(range, customFrom, customTo)
    return {
      ...range_dates,
      dealer: rooftopSlug === ALL_ROOFTOPS_VALUE ? undefined : rooftopSlug,
      dealerGroupId,
    }
  }, [range, customFrom, customTo, rooftopSlug, dealerGroupId])

  const [rooftops, setRooftops] = useState<RooftopOption[]>([])
  const [overview, setOverview] = useState<PhotosOverview | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Dealer dropdown options reload when the active group changes (scoped to it).
  useEffect(() => {
    if (dealerGroupId === null) {
      setRooftops([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const data = await listRooftops(dealerGroupId)
        if (!cancelled) setRooftops(data)
      } catch {
        // Filter dropdown is non-critical — fall back to All Dealers if
        // the rooftops fetch fails. The overview fetch surfaces its own error.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId])

  useEffect(() => {
    if (dealerGroupId === null) {
      setOverview(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const data = await getOverview(resolvedQuery)
        if (!cancelled) setOverview(data)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load overview")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // resolvedQuery is memoized — its identity changes only when from/to/dealer
    // /group change, so listing it directly satisfies react-hooks/exhaustive-deps
    // without causing extra re-fetches. dealerGroupId is read in the early guard.
  }, [resolvedQuery, dealerGroupId])

  const dealerOptions = useMemo(
    () => [{ slug: ALL_ROOFTOPS_VALUE, name: "All Dealers" }, ...rooftops],
    [rooftops],
  )

  const handleDealerChange = (next: string) => {
    const updated = new URLSearchParams(searchParams)
    if (next === ALL_ROOFTOPS_VALUE) updated.delete("rooftop")
    else updated.set("rooftop", next)
    setSearchParams(updated, { replace: true })
  }

  const handleRangeChange = (next: RangePreset) => {
    const updated = new URLSearchParams(searchParams)
    if (next === DEFAULT_RANGE) updated.delete("range")
    else updated.set("range", next)
    if (next !== "custom") {
      updated.delete("from")
      updated.delete("to")
    }
    setSearchParams(updated, { replace: true })
  }

  const handleCustomDateChange = (which: "from" | "to", value: string) => {
    const updated = new URLSearchParams(searchParams)
    updated.set("range", "custom")
    if (value) updated.set(which, value)
    else updated.delete(which)
    setSearchParams(updated, { replace: true })
  }

  const stats: StatSpec[] = [
    {
      label: "Total Sessions",
      value: overview?.total_captured_sessions ?? null,
      accentColor: null,
    },
    {
      label: "Units Captured",
      value: overview?.units_captured ?? null,
      accentColor: null,
    },
    {
      label: "Complete",
      value: overview?.shoots_complete ?? null,
      accentColor: THEME.auditTable.tabColors.completed,
    },
    {
      label: "Partial",
      value: overview?.shoots_partial ?? null,
      accentColor: THEME.auditTable.tabColors.pending,
      dividerAfter: true,
    },
    {
      label: "Ready to Push",
      value: overview?.sessions_ready_for_push ?? null,
      accentColor: THEME.auditTable.tabColors.pending,
    },
    {
      label: "Pushed",
      value: overview?.sessions_pushed ?? null,
      accentColor: THEME.auditTable.tabColors.completed,
    },
    {
      label: "Failed",
      value: overview?.sessions_failed ?? null,
      accentColor: THEME.auditTable.tabColors.discrepancies,
    },
  ]

  if (dealerGroupId === null) {
    return (
      <Layout>
        <div style={containerStyle}>
          <h1 style={{ ...titleStyle, marginBottom: THEME.spacing.md }}>Photo Overview</h1>
          <DealerGroupRequiredNotice surface="the photo overview" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div style={containerStyle}>
        <h1 style={titleStyle}>Photo Overview</h1>
        <p style={subtitleStyle}>
          Insights of capture activity across the dealer group.
        </p>

        {error && <div style={errorBannerStyle}>Failed to load: {error}</div>}

        <div style={statsRowStyle}>
          {stats.map((spec) => (
            <React.Fragment key={spec.label}>
              <StatCard spec={spec} loading={loading} />
              {spec.dividerAfter && <div style={tileDividerStyle} />}
            </React.Fragment>
          ))}
        </div>

        <div style={dealerSectionRowStyle}>
          <div style={filterColumnStyle}>
            <span style={fieldLabelStyle}>Dealer</span>
            <select
              value={rooftopSlug}
              onChange={(e) => handleDealerChange(e.target.value)}
              style={selectStyle}
              aria-label="Dealer"
            >
              {dealerOptions.map((d) => (
                <option key={d.slug} value={d.slug}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div style={filterColumnStyle}>
            <span style={fieldLabelStyle}>Date Range</span>
            <select
              value={range}
              onChange={(e) => handleRangeChange(e.target.value as RangePreset)}
              style={selectStyle}
              aria-label="Time frame"
            >
              {RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {range === "custom" && (
              <div style={customDateRowStyle}>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => handleCustomDateChange("from", e.target.value)}
                  style={dateInputStyle}
                  aria-label="From date"
                />
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => handleCustomDateChange("to", e.target.value)}
                  style={dateInputStyle}
                  aria-label="To date"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}

// ---------------------------------------------------------------------------
// Range resolution
// ---------------------------------------------------------------------------

function resolveRange(
  preset: RangePreset,
  customFrom: string,
  customTo: string,
): { from?: string; to?: string } {
  if (preset === "custom") {
    return {
      from: customFrom || undefined,
      to: customTo || undefined,
    }
  }
  const today = startOfDay(new Date())
  if (preset === "today") {
    return { from: formatDate(today), to: formatDate(today) }
  }
  if (preset === "yesterday") {
    const y = addDays(today, -1)
    return { from: formatDate(y), to: formatDate(y) }
  }
  if (preset === "last-month") {
    const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDayOfLastMonth = addDays(firstOfThisMonth, -1)
    const firstOfLastMonth = new Date(
      lastDayOfLastMonth.getFullYear(),
      lastDayOfLastMonth.getMonth(),
      1,
    )
    return { from: formatDate(firstOfLastMonth), to: formatDate(lastDayOfLastMonth) }
  }
  // last-7-days: today + 6 prior days, inclusive
  return { from: formatDate(addDays(today, -6)), to: formatDate(today) }
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d)
  out.setDate(out.getDate() + n)
  return out
}

function formatDate(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function StatCard({ spec, loading }: { spec: StatSpec; loading: boolean }) {
  const hasAccent = spec.accentColor !== null
  const cardStyle: React.CSSProperties = {
    width: 180,
    minHeight: 100,
    borderRadius: THEME.borderRadius.lg,
    padding: "16px 20px",
    background: hasAccent
      ? tint(spec.accentColor as string, 0.15)
      : THEME.colors.neutral.light.surface,
    border: `1px solid ${THEME.colors.silver}`,
    borderLeft: hasAccent
      ? `6px solid ${spec.accentColor}`
      : `1px solid ${THEME.colors.silver}`,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    fontFamily: THEME.fonts.primary,
    opacity: loading ? 0.75 : 1,
    filter: loading ? "saturate(0.4)" : "saturate(1)",
    transition: "opacity 300ms ease, filter 300ms ease",
  }
  return (
    <div style={cardStyle}>
      <div style={captionStyle}>{spec.label}</div>
      <div style={valueStyle}>
        {loading || spec.value === null ? "—" : spec.value.toLocaleString()}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  padding: THEME.spacing.lg,
  fontFamily: THEME.fonts.primary,
}

const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 500,
  letterSpacing: "-0.5px",
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.xs,
  marginBottom: THEME.spacing.lg,
}

const dealerSectionRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: THEME.spacing.md,
  flexWrap: "wrap",
}

const filterColumnStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  // Grow to fill available width so the row spans 100% of the page
  // even after the sidebar collapses; shrinks below 280px only by
  // wrapping to a second flex row.
  flex: "1 1 280px",
  minWidth: 280,
  gap: THEME.spacing.sm,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const customDateRowStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
  flexWrap: "wrap",
}

const selectStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
  flex: 1,
  minWidth: 160,
  cursor: "pointer",
}

const dateInputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
}

const errorBannerStyle: React.CSSProperties = {
  background: tint(THEME.colors.semantic.danger, 0.1),
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  color: THEME.colors.semantic.danger,
  fontSize: 13,
  marginBottom: THEME.spacing.md,
}

const statsRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: THEME.spacing.md,
  marginBottom: THEME.spacing.xl,
  alignItems: "stretch",
}

const tileDividerStyle: React.CSSProperties = {
  width: 1,
  alignSelf: "stretch",
  background: THEME.colors.silver,
  opacity: 0.6,
  marginLeft: THEME.spacing.xs,
  marginRight: THEME.spacing.xs,
}

const captionStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const valueStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  lineHeight: 1.1,
}

